import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KfAvatar } from "./KfAvatar";
import { KfCommentSection } from "./KfCommentSection";
import { Trash2, Heart, MessageCircle, Flag, Swords, Shield, Disc3, Users, Calendar, Clock } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel } from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import { toast } from "sonner";

const EVENT_ICONS = { raid: Swords, game: Shield, game_show: Disc3 };
const RAID_TYPE_LABELS = { alliance: "Alliance Raid", tournament: "Tournament Raid", party: "Party Raid", bbebbeg: "BBEBBEG Raid", dungeon_crawl: "Dungeon Crawl Raid" };
const LFG_KIND_LABELS = { looking_for_players: "Looking for Players", looking_for_table: "Looking for a Table" };
const LFG_SYSTEM_LABELS = { pf2e: "Pathfinder 2e", dnd5e: "D&D 5e", homebrew: "Homebrew", any: "Any System" };

// A PC token has to actually be PLACED on the map to count towards this tally — a bare
// membership row doesn't cut it — so it reflects who's really shown up to the table, not just
// who clicked "join" once.
const formatClassBreakdown = (breakdown) => {
  if (!breakdown || breakdown.length === 0) return "No one's placed a token yet";
  return breakdown.map((b) => `${b.count} ${b.class_name}${b.count > 1 ? "s" : ""}`).join(", ") + " are in this party";
};

const REPORT_REASONS = [
  { value: "spam", label: "Spam" },
  { value: "harassment", label: "Harassment" },
  { value: "inappropriate_for_minors", label: "Inappropriate for minors" },
  { value: "other", label: "Other" },
];

function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// A single Knights Fee feed post — author strip (links to their profile), text content, optional
// image, like/comment actions, and a delete button when the viewer is the author.
export function KfPostCard({ post, myUserId, onDeleted }) {
  const isMine = post.author.id === myUserId;
  const navigate = useNavigate();
  const [liked, setLiked] = useState(post.liked_by_me);
  const [likeCount, setLikeCount] = useState(post.like_count);
  const [likeBusy, setLikeBusy] = useState(false);
  const [commentCount, setCommentCount] = useState(post.comment_count);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [reported, setReported] = useState(false);
  const [rsvpBusy, setRsvpBusy] = useState(false);
  const [rsvpCount, setRsvpCount] = useState(post.rsvp_count || 0);
  const [rsvped, setRsvped] = useState(post.rsvped_by_me || false);

  const report = async (reason) => {
    try {
      await api.post(`/kf/posts/${post.id}/report`, { reason });
      setReported(true);
      toast.success("Thanks — a Knight of the Watch will take a look.");
    } catch {
      toast.error("Couldn't submit that report");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/kf/posts/${post.id}`);
      onDeleted(post.id);
    } catch {
      toast.error("Couldn't delete that post");
    }
  };

  const toggleLike = async () => {
    setLikeBusy(true);
    const nextLiked = !liked;
    setLiked(nextLiked);
    setLikeCount((c) => c + (nextLiked ? 1 : -1));
    try {
      if (nextLiked) await api.post(`/kf/posts/${post.id}/like`);
      else await api.delete(`/kf/posts/${post.id}/like`);
    } catch {
      setLiked(!nextLiked);
      setLikeCount((c) => c + (nextLiked ? -1 : 1));
      toast.error("Couldn't update that like");
    } finally {
      setLikeBusy(false);
    }
  };

  // RSVP buttons reuse the SAME join endpoints every other join flow already uses (invite-link,
  // Dashboard "Join a Raid/Game/Gameshow" dialogs) — zero duplicated join logic, same websocket
  // notifications as any other join. A lightweight, idempotent /rsvp ping fires alongside so the
  // host sees a live "N RSVP'd" counter build on their announcement post.
  const rsvp = async (action, path, body, destination) => {
    setRsvpBusy(true);
    try {
      const { data } = await api.post(path, body);
      toast.success(action);
      if (!rsvped) {
        setRsvped(true);
        setRsvpCount((c) => c + 1);
        api.post(`/kf/posts/${post.id}/rsvp`).catch(() => {});
      }
      navigate(destination(data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't join that");
    } finally {
      setRsvpBusy(false);
    }
  };

  const rsvpRaidGm = () => rsvp("Joined as a General!", `/raids/join/${post.event.gm_invite_code}`, undefined, (data) => `/raid/${data.raid.id}`);
  const rsvpRaidPlayer = () => rsvp("Joined as a Player!", `/games/join/${post.event.player_invite_code}`, {}, (data) => `/game/${data.game.id}`);
  const rsvpGame = () => rsvp("Joined as a Player!", `/games/join/${post.event.invite_code}`, {}, (data) => `/game/${data.game.id}`);
  const rsvpShowContestant = () => rsvp("Joined as a Contestant!", `/game-shows/join/${post.event.invite_code}`, {}, (data) => `/gameshow/${data.show_id}`);
  const rsvpShowSpectate = () => rsvp("You're in the audience!", `/game-shows/spectate/${post.event.invite_code}`, undefined, (data) => `/gameshow/${data.show_id}`);

  return (
    <div data-testid={`kf-post-${post.id}`} className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <Link to={`/knights-fee/u/${post.author.id}`} data-testid={`kf-post-author-link-${post.id}`} className="flex items-center gap-3 group">
          <KfAvatar name={post.author.name} avatarUrl={post.author.avatar_url} isMerchant={post.author.is_merchant} size="sm" />
          <div>
            <p className="text-sm font-semibold group-hover:text-[#D4AF37] transition-colors duration-150">{post.author.name}</p>
            <p className="text-[11px] text-[#f1e0c5]/40">{timeAgo(post.created_at)}</p>
          </div>
        </Link>
        {isMine ? (
          <button data-testid={`kf-post-delete-${post.id}`} onClick={remove} className="text-[#f1e0c5]/30 hover:text-[#D90429] transition-colors duration-150">
            <Trash2 className="w-4 h-4" />
          </button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`kf-post-report-button-${post.id}`}
                disabled={reported}
                className={`transition-colors duration-150 ${reported ? "text-[#D4AF37]" : "text-[#f1e0c5]/30 hover:text-[#D4AF37]"}`}
                title={reported ? "Reported" : "Report this post"}
              >
                <Flag className="w-4 h-4" fill={reported ? "currentColor" : "none"} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#1e1a14] border-white/10 text-[#f1e0c5]">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#f1e0c5]/50">Why are you reporting this?</DropdownMenuLabel>
              {REPORT_REASONS.map((r) => (
                <DropdownMenuItem key={r.value} data-testid={`kf-post-report-reason-${post.id}-${r.value}`} onClick={() => report(r.value)} className="text-sm focus:bg-white/10">
                  {r.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {post.content && <p className="text-sm text-[#f1e0c5]/85 whitespace-pre-wrap leading-relaxed">{post.content}</p>}
      {post.image_url && (
        <img src={post.image_url} alt="" className="rounded-lg w-full max-h-[420px] object-cover border border-white/10" data-testid={`kf-post-image-${post.id}`} />
      )}
      {post.event && post.post_type === "event_announcement" && (
        <div data-testid={`kf-post-event-${post.id}`} className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3 space-y-2.5">
          <div className="flex items-center gap-2.5">
            {(() => { const Icon = EVENT_ICONS[post.event.kind] || Swords; return <Icon className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />; })()}
            <div>
              <p className="text-sm font-semibold text-[#D4AF37]" data-testid={`kf-post-event-name-${post.id}`}>{post.event.name}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#f1e0c5]/40">
                {post.event.kind === "raid" ? (RAID_TYPE_LABELS[post.event.raid_type] || "Raid") : post.event.kind === "game" ? "Campaign" : "Game Show"}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {post.event.kind === "raid" && (
              <>
                <Button size="sm" disabled={rsvpBusy} data-testid={`kf-post-rsvp-gm-${post.id}`} onClick={rsvpRaidGm} className="rounded-full bg-[#D4AF37] text-black hover:bg-[#c19d2e] text-xs h-8">RSVP as GM</Button>
                {post.event.player_invite_code && (
                  <Button size="sm" disabled={rsvpBusy} variant="secondary" data-testid={`kf-post-rsvp-player-${post.id}`} onClick={rsvpRaidPlayer} className="rounded-full text-xs h-8">RSVP as Player</Button>
                )}
              </>
            )}
            {post.event.kind === "game" && (
              <Button size="sm" disabled={rsvpBusy} data-testid={`kf-post-rsvp-player-${post.id}`} onClick={rsvpGame} className="rounded-full bg-[#D4AF37] text-black hover:bg-[#c19d2e] text-xs h-8">Join as Player</Button>
            )}
            {post.event.kind === "game_show" && (
              <>
                <Button size="sm" disabled={rsvpBusy} data-testid={`kf-post-rsvp-contestant-${post.id}`} onClick={rsvpShowContestant} className="rounded-full bg-[#D4AF37] text-black hover:bg-[#c19d2e] text-xs h-8">Join as Contestant</Button>
                <Button size="sm" disabled={rsvpBusy} variant="secondary" data-testid={`kf-post-rsvp-spectate-${post.id}`} onClick={rsvpShowSpectate} className="rounded-full text-xs h-8">Watch as Spectator</Button>
              </>
            )}
          </div>
          <p className="flex items-center gap-1.5 text-[11px] text-[#f1e0c5]/40" data-testid={`kf-post-rsvp-count-${post.id}`}>
            <Users className="w-3.5 h-3.5" /> {rsvpCount > 0 ? `${rsvpCount} Knight${rsvpCount === 1 ? "" : "s"} RSVP'd` : "Be the first to RSVP"}
          </p>
        </div>
      )}
      {post.lfg && (
        <div data-testid={`kf-post-lfg-${post.id}`} className="rounded-lg border border-[#3F9142]/30 bg-[#3F9142]/5 p-3 space-y-2.5">
          <div className="flex items-center gap-2.5">
            <Users className="w-5 h-5 text-[#3F9142] flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[#3F9142]" data-testid={`kf-post-lfg-kind-${post.id}`}>
                {LFG_KIND_LABELS[post.lfg.kind] || "Looking for a Group"}{post.event ? ` — ${post.event.name}` : ""}
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[#f1e0c5]/40">{LFG_SYSTEM_LABELS[post.event?.system || post.lfg.system] || "Any System"}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 text-xs text-[#f1e0c5]/70">
            {post.lfg.day && <span data-testid={`kf-post-lfg-day-${post.id}`} className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {post.lfg.day}</span>}
            {post.lfg.time && <span data-testid={`kf-post-lfg-time-${post.id}`} className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.lfg.time}</span>}
          </div>
          {post.event && (
            <>
              <p className="text-xs text-[#f1e0c5]/70" data-testid={`kf-post-lfg-composition-${post.id}`}>
                {formatClassBreakdown(post.event.class_breakdown)}{post.event.party_size ? ` (party of ${post.event.party_size})` : ""}
              </p>
              {post.event.can_self_join ? (
                <Button
                  size="sm"
                  disabled={rsvpBusy || (post.event.kind === "raid" && !post.event.player_invite_code)}
                  data-testid={`kf-post-lfg-join-${post.id}`}
                  onClick={post.event.kind === "raid" ? rsvpRaidPlayer : rsvpGame}
                  className="rounded-full bg-[#3F9142] text-white hover:bg-[#357539] text-xs h-8"
                >
                  {post.event.kind === "raid" && !post.event.player_invite_code ? "Host hasn't opened a table yet" : "Join This Table"}
                </Button>
              ) : (
                <p className="text-[11px] text-[#f1e0c5]/40" data-testid={`kf-post-lfg-full-${post.id}`}>Party full — ask the GM to invite you directly</p>
              )}
            </>
          )}
        </div>
      )}
      <div className="flex items-center gap-5 pt-1">
        <button
          data-testid={`kf-post-like-button-${post.id}`}
          onClick={toggleLike}
          disabled={likeBusy}
          className={`flex items-center gap-1.5 text-xs transition-colors duration-150 ${liked ? "text-[#D90429]" : "text-[#f1e0c5]/50 hover:text-[#D90429]"}`}
        >
          <Heart className="w-4 h-4" fill={liked ? "currentColor" : "none"} />
          <span data-testid={`kf-post-like-count-${post.id}`}>{likeCount}</span>
        </button>
        <button
          data-testid={`kf-post-comment-toggle-${post.id}`}
          onClick={() => setCommentsOpen((v) => !v)}
          className={`flex items-center gap-1.5 text-xs transition-colors duration-150 ${commentsOpen ? "text-[#D4AF37]" : "text-[#f1e0c5]/50 hover:text-[#D4AF37]"}`}
        >
          <MessageCircle className="w-4 h-4" />
          <span data-testid={`kf-post-comment-count-${post.id}`}>{commentCount}</span>
        </button>
      </div>
      {commentsOpen && (
        <KfCommentSection postId={post.id} myUserId={myUserId} onCommentCountChange={(delta) => setCommentCount((c) => c + delta)} />
      )}
    </div>
  );
}
